import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../button/Button";
import { ClosedDay } from "../../features/closed-days/models/ClosedDay";
import { addDays, toYmd } from "../../features/calendar/utils/calendar-range";

interface CloseDayDialogProps {
  day: Date;
  /** Present when the day is already closed — the dialog then offers reopen instead of close. */
  closed?: ClosedDay;
  busy: boolean;
  onClose: () => void;
  onSubmitClose: (reason: string, cancelOccurrences: boolean, endDate: string | null) => void;
  onReopen: () => void;
}

// Mirrors the backend cap: a closure may span a quarter at most.
const MAX_RANGE_DAYS = 92;

/** Mark a day — or a whole date range (holidays) — closed, or reopen a closed day. */
const CloseDayDialog: React.FC<CloseDayDialogProps> = ({
  day,
  closed,
  busy,
  onClose,
  onSubmitClose,
  onReopen,
}) => {
  const { t, i18n } = useTranslation();
  const [reason, setReason] = useState("");
  const [cancelOccurrences, setCancelOccurrences] = useState(true);
  const [endDate, setEndDate] = useState("");

  const dayYmd = toYmd(day);
  const maxEndYmd = toYmd(addDays(day, MAX_RANGE_DAYS - 1));
  const isRange = endDate !== "" && endDate > dayYmd;

  const dayLabel = new Intl.DateTimeFormat(i18n.language, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(day);

  const isReopen = Boolean(closed);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 dark:bg-black/70">
      <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-xl lg:p-6 dark:bg-primary-10">
        <h3 className="text-grey-10 dark:text-grey-90">
          {isReopen ? t("calendar.closedDay.reopenTitle") : t("calendar.closedDay.closeTitle")}
        </h3>
        <p className="mt-1 text-sm text-grey-30 dark:text-grey-70">{dayLabel}</p>

        {isReopen ? (
          <div className="mt-4">
            {closed?.reason && (
              <p className="text-sm text-grey-20 dark:text-grey-80">{closed.reason}</p>
            )}
            <p className="mt-2 text-sm text-grey-30 dark:text-grey-70">
              {t("calendar.closedDay.reopenMessage")}
            </p>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <div>
              <label className="mb-1 block text-sm text-grey-20 dark:text-grey-80">
                {t("calendar.closedDay.reasonLabel")}
              </label>
              <input
                type="text"
                className="input"
                value={reason}
                maxLength={500}
                placeholder={t("calendar.closedDay.reasonPlaceholder")}
                onChange={(e) => setReason(e.target.value)}
                disabled={busy}
              />
            </div>
            <div>
              <label
                htmlFor="close-day-until"
                className="mb-1 block text-sm text-grey-20 dark:text-grey-80"
              >
                {t("calendar.closedDay.endDateLabel")}
              </label>
              <input
                id="close-day-until"
                type="date"
                className="input"
                value={endDate}
                min={dayYmd}
                max={maxEndYmd}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={busy}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-grey-20 dark:text-grey-80">
              <input
                type="checkbox"
                checked={cancelOccurrences}
                onChange={(e) => setCancelOccurrences(e.target.checked)}
                disabled={busy}
              />
              {t(isRange ? "calendar.closedDay.cancelOccurrencesRange" : "calendar.closedDay.cancelOccurrences")}
            </label>
            <p className="text-xs text-grey-30 dark:text-grey-70">
              {t("calendar.closedDay.attendanceNote")}
            </p>
          </div>
        )}

        <div className="mt-5 flex justify-end gap-3">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          {isReopen ? (
            <Button onClick={onReopen} disabled={busy}>
              {t("calendar.closedDay.reopenAction")}
            </Button>
          ) : (
            <Button
              onClick={() => onSubmitClose(reason, cancelOccurrences, isRange ? endDate : null)}
              disabled={busy}
            >
              {t(isRange ? "calendar.closedDay.closeActionRange" : "calendar.closedDay.closeAction")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CloseDayDialog;
